import React from 'react';
import { useLocale, useAssets } from 'hooks';
import { useFormContext } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import {
  Page,
  Stepper,
  StepFormVerifier,
  StepViewReport,
  StepFinishScreen,
  StepFormScanAndDisplay,
  ScanAsset,
} from 'ui';
import { AssetStatuses } from 'hooks/useAssets/useAssets.types';

import { IssueVerificationProps } from './IssueVerification.types';

export const IssueVerification = ({ isLoading, onSubmit }: IssueVerificationProps) => {
  const { formatMessage } = useLocale();
  const { setValue, watch } = useFormContext();
  const { getAssetById } = useAssets();
  const { replace } = useHistory();

  const assetId = watch('id');

  const handleIdRead = (data: string) => {
    const asset = getAssetById(data);
    if (asset && asset.status !== AssetStatuses.INSTALLED) {
      replace(`/details/${data}`);
    }
    setValue('id', data);
  };

  const handleCodeRead = ({ goToNextStep, field }: { goToNextStep: VoidFunction; field: string }) => (data: string) => {
    setValue(field, data);
    goToNextStep();
  };

  return (
    <Page title={formatMessage({ id: 'verify_installation.page_title' })}>
      <Stepper
        steps={[
          {
            label: formatMessage({ id: 'issue_battery.steps.scan' }),
            content: goToNextStep => <StepFormScanAndDisplay handleRead={handleIdRead} goToNextStep={goToNextStep} />,
          },
          {
            label: formatMessage({ id: 'activateAsset.steps.identifyMeter' }),
            content: (goToNextStep, goToPreviousStep) => (
              <ScanAsset
                key="meter"
                handleRead={handleCodeRead({ goToNextStep, field: 'meterSerial' })}
                goToPreviousStep={goToPreviousStep}
                goToNextStep={goToNextStep}
                delayNextScan
                isEan
              />
            ),
          },
          {
            label: formatMessage({ id: 'issue_battery.steps.enter_details' }),
            content: (goToNextStep, goToPreviousStep) => (
              <StepFormVerifier goToNextStep={goToNextStep} goToPreviousStep={goToPreviousStep} onSubmit={onSubmit} />
            ),
          },
          {
            label: formatMessage({ id: 'issue_battery.steps.view_report' }),
            content: goToNextStep => <StepViewReport goToNextStep={goToNextStep} isLoading={isLoading} />,
          },
        ]}
        finishStep={<StepFinishScreen id={assetId} showAttachmentMessage={false} />}
      />
    </Page>
  );
};
